import React, {useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {Button, Col, Container, Row} from 'react-bootstrap';
import {ConfirmationDialog} from "../../Common/ConfirmationDialog";
import {GenericModelViewer} from "../../Common/GenericModelViewer";
import {StreamBankPageHeader} from "../../Common/StreamBankPageHeader";
import {Glyphicon} from "../../Common/GlyphIcon";
import {DocumentUtils, ObjectUtils} from "../../../Utils";
import {useDefaultErrorHandler} from "../../../Hooks/ErrorHooks";
import {usePermission} from "../../../Hooks/PermissionHooks";
import {useCheckAuth} from "../../../Hooks/RouteHooks";
import {useMakePageRequest} from "../../../Hooks/RequestHooks";

import {User} from "../../../Models/StreamBank/User";
import {service as UserRoleService} from '../../../Services/UserRoleService';
import {service as UserService} from '../../../Services/UserService';


import {useContextIndependentPage} from "../../../Hooks/ContextBuilderHooks";
import {useClassNames} from "../../../Hooks/StyleHooks"

let model = new User();


export const UsersViewPage = props => {
    useCheckAuth();
    useContextIndependentPage(props);

    const classNames = useClassNames('sb-users-view-page');
    const makePageRequest = useMakePageRequest();
    const permission = usePermission();
    const defaultErrorHandler = useDefaultErrorHandler();

    const currentUser = useSelector(state => state.userState.user);

    const [user, setUser] = useState(null);
    const [existingUser, setExistingUser] = useState(null);
    const [userValidation, setUserValidation] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);

    const userId = props.match.params.userId;

    useEffect(() => {
        loadData();
    }, [userId]);

    const loadData = () => {
        if(userId === "new") {
            let orgId = null;
            if(currentUser && currentUser.organizationId) {
                //if we have a user pulled. let's default to their orgId.
                orgId = currentUser.organizationId;
            }

            makePageRequest(
                () => Promise.all([
                    UserRoleService.Get()
                ]),
                ([userRoles]) => {
                    setUserRoles(userRoles.result);
                    setEditMode(true);
                    setUser({organizationId: orgId});
                }
            );
        } else {
            makePageRequest(
                () => Promise.all([
                    UserService.FindById(userId, false),
                    UserRoleService.Get()
                ]),
                ([user, userRoles]) => {
                    setUser(user.result);
                    setUserRoles(userRoles.result);
                    setEditMode(false);
                }
            );
        }
    };

    const canEdit = () => {
        if(user && currentUser) {
            if(user.userId == currentUser.userId) {
                if(user.userRoleId == currentUser.userRoleId) {
                    return true; //you can edit your own user.
                }
                return false; // but can't change the role.
            }

            if(permission('update_user')) {
                let userRole = userRoles.find((x) => x.userRoleId == user.userRoleId);

                if(userRole) {
                    if(permission('update_user[' + userRole.code + ']')) {
                        return true;
                    }
                }
            }
        }
        return false;
    };

    const canDelete = () => {
        if(user && currentUser) {
            if(user.userId != currentUser.userId) { // you can't delete yourself
                if(permission('delete_user')) {
                    let userRole = userRoles.find((x) => x.userRoleId == user.userRoleId);

                    if(userRole) {
                        if(permission('update_user[' + userRole.code + ']')) {
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    };

    const enterEditMode = () => {
        setEditMode(true);
        setExistingUser(ObjectUtils.Clone(user));
    };

    const exitEditMode = () => {
        if(existingUser) {
            setEditMode(false);
            setUser(ObjectUtils.Clone(existingUser));
            setExistingUser(null);
        } else {
            let newPath = window.location.pathname.replace("/users/new", "/users");
            DocumentUtils.changePage(newPath);
        }
    };

    const updateUser = (user) => {
        setUser(user);
    };

    const saveUser = () => {
        if(user.userId) {
            makePageRequest(
                () => UserService.UpdateUser(user),
                (x) => {
                    setEditMode(false);
                    setUser(x.result);
                },
                (x) => {
                    defaultErrorHandler(x);
                }
            );
        } else {
            makePageRequest(
                () => UserService.CreateUser(user),
                (x) => {
                    let newPath = window.location.pathname.replace("new", x.result.userId);
                    DocumentUtils.changePage(newPath);
                },
                (x) => {
                    defaultErrorHandler(x);
                }
            );
        }
    };

    const deleteUser = () => {
        makePageRequest(
            () => UserService.DeleteUser(user.userId),
            (x) => DocumentUtils.changePage("/users"),
        );
    };

    const updateValidation = (validation, editorGuid) => {
        setUserValidation(ObjectUtils.Clone(validation));
    };

    const isValid = () => {
        for(let propertyName in userValidation) {
            let validation = userValidation[propertyName];

            if(!validation.valid) {
                return false;
            }
        }

        return true;
    };

    // don't render anything until we are loaded.
    if(user === null) {
        return null;
    }

    return <Container className={classNames}>
        <Row>
            <StreamBankPageHeader
                Title={user.userId ? user.name: "New User"}
            />
            <ConfirmationDialog
                Title="Confirmation"
                Body="Are you sure you want to delete this user?"
                Display={deleteDialog}
                OnClose={() => setDeleteDialog(false)}
                OnConfirm={() => deleteUser()}
            />
            <Col xs={12} md={6}>
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        {
                            editMode ? [
                                    <Button
                                        variant="save"
                                        key={1}
                                        onClick={() => saveUser()}
                                        disabled={!isValid() || !canEdit()}
                                    >
                                        Save
                                    </Button>,
                                    <Button
                                        variant="cancel"
                                        key={2}
                                        onClick={() => exitEditMode()}
                                    >
                                        Cancel
                                    </Button>,
                                    userId !== 'new' && canDelete() ?
                                        <Button
                                            variant="delete"
                                            key={3}
                                            onClick={() => setDeleteDialog(true)}
                                        >
                                            Delete
                                        </Button> :
                                        null
                                ] :
                                (
                                    canEdit() ?
                                        <Button
                                            variant="link"
                                            key={1}
                                            onClick={() => enterEditMode()}
                                        >
                                            <Glyphicon glyph="pencil" />Edit
                                        </Button>:
                                        (permission('can_update_other_users') ?
                                            <Button
                                                disabled="disabled"
                                                variant="link"
                                                key={1}
                                                title="You do not have permission to edit this user."
                                            >
                                                <Glyphicon glyph="pencil" />Edit
                                            </Button>
                                            : null)
                                )
                        }
                    </div>
                    {user ? user.name : ""}
                </h2>

                {
                    user ?
                        <GenericModelViewer
                            IsNewModel={userId === "new"}
                            EditMode={editMode}
                            Data={user}
                            Update={updateUser}
                            OnValidationUpdate={updateValidation}
                            Model={model}
                        />:
                        null
                }
            </Col>
            <Col xs={12} md={6} />
        </Row>
    </Container>;
};

